import { cloneDeep } from 'lodash/fp';
import { PANZOOM } from '@/constants.js';
import components from './components-config.js';
import boards from './boards-config.js';
import assets from './assets-config.js';
import elements from './elements-config.js';
import notes from './notes-config.js';
import jumpers from './jumpers-config.js';
import branches from './branches-config.js';
import connections from './connections-config.js';
import comments from './comments-config.js';
import updater from '../updater.js';
import { has } from '../../../utils.js';

const state = {
  grid: { snap: null, show: null, size: null },
  spellcheck: { show: false },
  exportOptions: {
    json: { boards: null, includeAssets: true },
    image: { boards: null, type: 'jpg', size: 'small', printFriendly: false },
    pdf: { boards: null, printFriendly: false },
    selectedTab: 'JSON',
  },
  cursorMode: PANZOOM.CURSOR_MODE.SELECT,
};

const getters = {
  /** Returns the cursor mode of the view */
  getCursorMode(state) {
    return state.cursorMode;
  },

  /**
   * Return whether the selection is empty
   * @return {boolean}      Whether the selection is empty or not
   */
  getIsSelectionEmpty(state, getters, rootState, rootGetters) {
    return (
      rootGetters['project/config/elements/getIsSelectionEmpty'] &&
      rootGetters['project/config/notes/getIsSelectionEmpty'] &&
      rootGetters['project/config/jumpers/getIsSelectionEmpty'] &&
      rootGetters['project/config/branches/getIsSelectionEmpty'] &&
      rootGetters['project/config/connections/getIsSelectionEmpty'] &&
      rootGetters['project/config/comments/getIsSelectionEmpty']
    );
  },

  /**
   * Return all selections (elements-notes-connections)
   * @return {object}       The selections in an object
   */
  getSelections(state, getters, rootState, rootGetters) {
    return {
      elements: rootGetters['project/config/elements/getSelections'],
      notes: rootGetters['project/config/notes/getSelections'],
      jumpers: rootGetters['project/config/jumpers/getSelections'],
      branches: rootGetters['project/config/branches/getSelections'],
      connections: rootGetters['project/config/connections/getSelections'],
      comments: rootGetters['project/config/comments/getSelections'],
    };
  },

  /**
   * Return if the current selection is multiselection
   * @return {boolean}      Whether there is a multiselection
   */
  getIsMultiselection(state, getters) {
    console.log('GET IS MULTI SELECTION GETTER');
    const selections = getters.getSelections;
    console.log('Selections:', selections);
    console.log('Object entries:', Object.values(selections));
    console.log(
      'Result is:',
      Object.values(selections).reduce((sum, arr) => sum + arr.length, 0)
    );
    return (
      Object.values(selections).reduce((sum, arr) => sum + arr.length, 0) > 1
    );
  },

  /**
   * Return the grid options
   * @return {Object}     The grid options { snap, show, size }
   */
  getGrid(state) {
    return state.grid;
  },

  /**
   * Return the export options
   * @return {Object}     The export options { snap, show, size }
   */
  getExportOptions(state) {
    return state.exportOptions;
  },

  /**
   * Return the spellcheck options
   * @return {Object}     The spellcheck options { show }
   */
  getSpellcheck(state) {
    return state.spellcheck;
  },
};

const actions = {
  setCursorMode(context, { value, apply }) {
    const patch = [
      // Change grid settings
      {
        op: 'add',
        path: `/config/cursorMode`,
        value,
      },
    ];
    updater.add(patch);
    updater.complete({ apply, save: false });
  },

  /**
   * Deselect all nodes of elements, notes and connections.
   * @param   {boolean} apply          Whether to apply the patch after finishing
   */
  deselectAll(context, { apply }) {
    if (context.getters.getIsSelectionEmpty) return;
    context.dispatch(
      'project/config/elements/deselectAll',
      { apply: false },
      { root: true }
    );
    context.dispatch(
      'project/config/notes/deselectAll',
      { apply: false },
      { root: true }
    );
    context.dispatch(
      'project/config/jumpers/deselectAll',
      { apply: false },
      { root: true }
    );
    context.dispatch(
      'project/config/branches/deselectAll',
      { apply: false },
      { root: true }
    );
    context.dispatch(
      'project/config/connections/deselectAll',
      { apply: false },
      { root: true }
    );
    context.dispatch(
      'project/config/comments/deselectAll',
      { apply: false },
      { root: true }
    );
    updater.complete({ apply });
  },

  /**
   * Move the selection relative to its current position
   * @param {number} x
   * @param {number} y
   * @param {boolean} apply
   * @param {boolean} save
   */
  moveSelections(context, { x, y, apply, save = true }) {
    const selectables = ['elements', 'notes', 'jumpers', 'branches'];
    // Account for grid snap
    const { snap, size } = context.getters.getGrid;
    selectables.forEach(selectable => {
      // Get the ids of the selected items
      const selections =
        context.rootGetters[`project/config/${selectable}/getSelections`];
      // Get the nodes of the selected ids
      const nodes = context.rootGetters[`project/data/${selectable}/getNodes`](
        selections
      );

      // Move the nodes
      nodes.forEach(node => {
        console.log('Moving selections');
        let newX = node.x + x;
        let newY = node.y + y;
        if (snap) {
          if (x > 0) {
            newX -= newX % size;
          } else if (x < 0 && newX % size) {
            newX += size - (newX % size);
          }
          if (y > 0) {
            newY -= newY % size;
          } else if (y < 0 && newY % size) {
            newY += size - (newY % size);
          }
        }
        const props = { x: newX, y: newY };
        context.dispatch(
          `project/data/${selectable}/update`,
          { id: node.id, props, apply: false },
          { root: true }
        );
      });
    });

    // Handle the comments separately
    const commentSelections = cloneDeep(
      context.rootGetters['project/config/comments/getSelections']
    );

    const nodes = context.rootGetters['project/data/comments/getNodes'](
      commentSelections
    );
    // Move the comments
    nodes.forEach(node => {
      let newX = node.position.x + x;
      let newY = node.position.y + y;
      if (snap) {
        if (x > 0) {
          newX -= newX % size;
        } else if (x < 0 && newX % size) {
          newX += size - (newX % size);
        }
        if (y > 0) {
          newY -= newY % size;
        } else if (y < 0 && newY % size) {
          newY += size - (newY % size);
        }
      }
      const props = {
        position: {
          ...node.position,
          x: newX,
          y: newY,
        },
      };
      context.dispatch(
        'project/data/comments/update',
        { id: node.id, props, apply: false },
        { root: true }
      );
    });

    updater.complete({ apply, save });
  },

  /**
   * Delete all selections for elements, notes, jumpers, branches and connections.
   * @param   {boolean} apply          Whether to apply the patch after finishing
   */
  deleteSelections(context, { apply }) {
    if (context.getters.getIsSelectionEmpty) return;
    let connectionSelections = cloneDeep(
      context.rootGetters['project/config/connections/getSelections']
    );
    const elementSelections = cloneDeep(
      context.rootGetters['project/config/elements/getSelections']
    );
    const noteSelections = cloneDeep(
      context.rootGetters['project/config/notes/getSelections']
    );
    const jumperSelections = cloneDeep(
      context.rootGetters['project/config/jumpers/getSelections']
    );
    const branchSelections = cloneDeep(
      context.rootGetters['project/config/branches/getSelections']
    );
    const commentSelections = cloneDeep(
      context.rootGetters['project/config/comments/getSelections']
    );
    // Get starting Element
    const startingElemId =
      context.rootGetters['project/data/elements/getStartingElement'];

    const attributeIds = [];
    // Add all the connections of selected elements to the delete selection
    elementSelections.forEach(id => {
      const c = context.rootGetters['project/data/connections/getConnections'](
        id
      );
      connectionSelections = [...new Set([...connectionSelections, ...c])];
      // Clear jumpers
      context.dispatch(
        'project/data/jumpers/clearElementJumpers',
        { id, apply: false },
        { root: true }
      );
      // Check if StartingElement and set it to null
      if (id === startingElemId) {
        context.dispatch(
          'project/data/elements/setStartingElement',
          { id: null, apply: false },
          { root: true }
        );
      }
      attributeIds.push(
        ...context.rootGetters['project/data/elements/getAttributes'](id)
      );
    });

    if (elementSelections?.length > 0) {
      // Remove any mentions in elements, conditions, connections
      ['elements', 'conditions', 'connections'].forEach(name => {
        context.dispatch(
          `project/data/${name}/deleteMentions`,
          { ids: elementSelections, type: 'element', apply: false },
          { root: true }
        );
      });

      // Remove any mentions in clipboard
      context.dispatch(
        'application/clipboard/deleteMentions',
        { ids: elementSelections, type: 'element' },
        { root: true }
      );

      // Remove elements from jumper references in clipboard
      context.dispatch(
        'application/clipboard/deleteElementsFromJumpers',
        { elementIds: elementSelections },
        { root: true }
      );
    }

    // Add all the connections of selected jumpers to the delete selection
    jumperSelections.forEach(id => {
      const c = context.rootGetters['project/data/connections/getConnections'](
        id
      );
      connectionSelections = [...new Set([...connectionSelections, ...c])];
    });

    const conditions = [];
    // Add all the connections of selected branches to the delete selection
    branchSelections.forEach(id => {
      const c = context.rootGetters['project/data/connections/getConnections'](
        id
      );
      connectionSelections = [...new Set([...connectionSelections, ...c])];

      // Add all the connections of the conditions to the delete selection
      conditions.push(
        ...context.rootGetters['project/data/branches/getConditions'](id)
      );
    });

    // Add all the connections of conditions to the delete selection
    conditions.forEach(id => {
      const c = context.rootGetters['project/data/connections/getConnections'](
        id
      );
      connectionSelections = [...new Set([...connectionSelections, ...c])];
    });

    // Add all children of comments to the delete selection
    const childComments = [];
    commentSelections.forEach(id => {
      const comment = context.rootGetters['project/data/comments/getNode'](id);
      if (has(comment, 'children')) {
        const { children } = comment;
        childComments.push(...children);
      }
    });
    console.log('comment children:', childComments);

    // Deselect everything before deleting
    context.dispatch('deselectAll', { apply: false });

    // Remove the references to these connections (the elements/branches outputs)
    context.dispatch(
      'project/data/connections/deleteReferences',
      { ids: connectionSelections, apply: false },
      { root: true }
    );

    context.dispatch(
      'project/data/connections/deleteMany',
      { ids: connectionSelections, apply: false },
      { root: true }
    );
    context.dispatch(
      'project/data/elements/deleteMany',
      { ids: elementSelections, apply: false },
      { root: true }
    );
    context.dispatch(
      'project/data/attributes/deleteMany',
      { ids: attributeIds, deleteReference: false, apply: false },
      { root: true }
    );
    context.dispatch(
      'project/data/notes/deleteMany',
      { ids: noteSelections, apply: false },
      { root: true }
    );
    context.dispatch(
      'project/data/jumpers/deleteMany',
      { ids: jumperSelections, apply: false },
      { root: true }
    );
    context.dispatch(
      'project/data/conditions/deleteMany',
      { ids: conditions, apply: false },
      { root: true }
    );
    context.dispatch(
      'project/data/branches/deleteMany',
      { ids: branchSelections, apply: false },
      { root: true }
    );
    // Delete the child comments (do not delete their parent references)
    context.dispatch(
      'project/data/comments/deleteMany',
      { ids: childComments, apply: false, deleteReference: false },
      { root: true }
    );
    // Delete the threads
    context.dispatch(
      'project/data/comments/deleteMany',
      { ids: commentSelections, apply: false },
      { root: true }
    );
    // Delete the references to threads from other objects
    context.dispatch(
      'project/data/comments/deleteReferences',
      { ids: commentSelections, apply: false },
      { root: true }
    );
    // If a deleted comment is open close it
    const open = context.rootGetters['project/config/comments/getOpen'];
    if (commentSelections.includes(open)) {
      context.dispatch(
        'project/config/comments/setOpen',
        { id: null, apply: false },
        { root: true }
      );
    }

    updater.complete({ apply });
  },

  /**
   * Set the grid settings (merge with old values)
   * @param {Object}  props          The grid settings to change (one or more of { snap, show, size })
   * @param {boolean} apply          Whether to apply the patch
   */
  setGrid(context, { props, apply }) {
    // Merge with old values
    let { grid } = context.state;
    grid = { ...grid, ...props };
    const patch = [
      // Change grid settings
      {
        op: 'replace',
        path: `/config/grid`,
        value: grid,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Set spellchecking settings (merge with old values)
   * @param {Object}  props          The spell settings to change (one or more of { show })
   * @param {boolean} apply          Whether to apply the patch
   */
  setSpellcheck(context, { props, apply }) {
    // Merge with old values
    let { spellcheck } = context.state;
    spellcheck = { ...spellcheck, ...props };
    const patch = [
      // Change spellcheck settings
      {
        op: 'replace',
        path: `/config/spellcheck`,
        value: spellcheck,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },

  /**
   * Set export options (merge with old values)
   * @param {Object}  props          The export options to change (one or more of { json, image, pdf })
   * @param {boolean} apply          Whether to apply the patch
   */
  setExportOptions(context, { props, apply }) {
    // Merge with old values
    let { exportOptions } = context.state;
    exportOptions = { ...exportOptions, ...props };
    const patch = [
      // Change export options
      {
        op: 'replace',
        path: `/config/exportOptions`,
        value: exportOptions,
      },
    ];
    updater.add(patch);
    updater.complete({ apply });
  },
};

const modules = {
  components,
  boards,
  assets,
  elements,
  notes,
  jumpers,
  branches,
  connections,
  comments,
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations: {},
  actions,
  modules,
};

export default module;
